import type { Customer } from "./Customer";
import { createResourceStore } from "@/lib/resource";
import type { Shop } from "./Shop";
import type { Sale } from "./Sale";
import type { User } from "./User";
import type { FinanceTransaction } from "./FinanceTransaction";
import type { FinanceDebt } from "./FinanceDebt";

export interface Order {
  id: string;
  status: string;
  delivery_status: string;
  delivery_fee: number;
  discount: number;
  tax: number;
  shop_id: string;
  customer_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  shop: Shop;
  customer: Customer;
  user: User;
  sales: Sale[];
  transactions: FinanceTransaction[];
  debts: FinanceDebt[];
  customer_name: string;
}

export const useOrder = createResourceStore<Order>("orders", () => ({
  stats: {
    total_orders: 0,
    total_revenue: 0.0,
    completed_orders: 0,
    pending_orders: 0,
    cancelled_orders: 0,
    series: {
      labels: [],
      datasets: [],
    },
    delivery_status_repartition: {},
  },
  transform: (order: any) => {
    return {
      ...order,
      amount_given: parseFloat(order.amount_given),
      change_given: parseFloat(order.change_given),
      delivery_fee: parseFloat(order.delivery_fee),
      discount: parseFloat(order.discount),
      tax: parseFloat(order.tax),
    };
  },
}));

export const getTotal = (sales: Sale[]) => {
  return (sales || [])
    .map((item) => item.quantity * item.selling_price)
    .reduce((sum, item) => sum + item, 0);
};

export const getPurchaseTotal = (sales: Sale[]) => {
  return (sales || [])
    .map((item) => item.quantity * item.salable.unit_price)
    .reduce((sum, item) => sum + item, 0);
};

const sanitizeNumberString = (value: string | number) => {
  return parseFloat((value == "" ? 0 : value) as any);
};

export const getNetAmount = (order: any) => {
  return (
    getTotal(order.sales) +
    sanitizeNumberString(order.delivery_fee) +
    getTotal(order.sales) * 0.01 * sanitizeNumberString(order.tax) -
    sanitizeNumberString(order.discount)
  );
};

export const getProfit = ({
  sales,
  discount,
  delivery_fee,
  tax,
}: {
  sales: Sale[];
  discount: number;
  delivery_fee: number;
  tax: number;
}) => {
  return (
    getNetAmount({ sales, discount, delivery_fee, tax } as any) -
    getPurchaseTotal(sales)
  );
};
